import { useGetApplys } from "@api";
import type { IFormGenContext, TJSONSchema } from "@artifact-management/json-dynamic-form";
import type { IApply } from "@bbj-types/apply";
import type { ISettingValueMaster } from "@bbj-types/setting";
import { getSettingsLabelFromValue } from "@candidate/components/utils/formatTools";
import { useGlobalContext } from "@root/context";
import { TRAINING_OFFER_DURATION_UNIT_LABELS } from "@root/utils/constants/trainingOffer";
import { type MouseEvent, useLayoutEffect, useMemo, useState } from "react";

const DEFAULT_FILTERS = {
  kind: "job",
  processed: true,
  applicable: true,
  sort: { updatedAt: -1, scoring: -1, travelTime: -1 },
  limit: 0,
} as const;

export type TJobApplyOption = {
  label: string;
  subText: string;
  value: string;
};

const isJobApplyOption = (value: unknown): value is TJobApplyOption => {
  return typeof value === "object" && value !== null && "value" in value && typeof value.value === "string";
};

export const isJobApplyOptionArray = (value: unknown): value is TJobApplyOption[] => {
  return Array.isArray(value) && value.every(isJobApplyOption);
};

export const getApplySubtext = (apply: IApply, setting?: ISettingValueMaster[]) => {
  let text = apply.offer?.enterprise?.name ?? "";
  const contractType = apply.offer.contractType?.[0];
  let contractText = contractType ? getSettingsLabelFromValue(contractType, setting) : "";
  const { duration, unit } = apply.offer.contractDuration ?? {};
  if (duration && unit) {
    contractText += `${contractText ? " " : ""}${duration} ${TRAINING_OFFER_DURATION_UNIT_LABELS[unit]}`;
  }
  if (contractText) {
    text += (text ? " . " : "") + contractText;
  }
  if (apply.offer?.address?.city) {
    text += (text ? " . " : "") + apply.offer.address.city;
  }
  return text;
};

const getApplyOption = (apply: IApply, setting?: ISettingValueMaster[]): TJobApplyOption => {
  return {
    label: apply.offer.name,
    subText: getApplySubtext(apply, setting),
    value: apply._id,
  };
};

export const useFormGenInputJobApply = (
  jobApplyOptions: Exclude<TJSONSchema["jobApply"], undefined>,
  context: IFormGenContext | undefined
) => {
  const { settingsV2 } = useGlobalContext();
  const [{ data, loading }, getApplies] = useGetApplys();
  const [applies, setApplies] = useState<IApply[]>([]);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const contractTypeSettings = settingsV2.settingsContractTypes?.values;

  const options = useMemo(() => {
    return applies.map((apply) => getApplyOption(apply, contractTypeSettings));
  }, [applies, contractTypeSettings]);

  const handleNewApplyClick = (evt: MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    setAutocompleteOpen(false);
    setApplyModalOpen(true);
  };

  const handleNewApply = (apply: IApply) => {
    const option = getApplyOption(apply, contractTypeSettings);
    setApplies((prev) => [apply, ...prev]);
    setApplyModalOpen(false);
    return option;
  };

  useLayoutEffect(() => {
    if (!context?.candidateId) {
      return;
    }
    void getApplies({ ...DEFAULT_FILTERS, candidate: context.candidateId, status: jobApplyOptions.status });
  }, [context?.candidateId, jobApplyOptions.status, getApplies]);

  useLayoutEffect(() => {
    if (data) {
      setApplies(data.data);
    }
  }, [data]);

  return {
    autocompleteOpen,
    setAutocompleteOpen,
    handleNewApplyClick,
    options,
    loading,
    applyModalOpen,
    setApplyModalOpen,
    handleNewApply,
  };
};
